import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isActive, loading } = useAuth();
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      // User not logged in - redirect to home and show message
      setShowToast(true);
      navigate('/', { replace: true });
      
      // Scroll to membership section after navigation
      setTimeout(() => {
        const membershipSection = document.getElementById('membership-tiers');
        if (membershipSection) {
          membershipSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);

      // Hide toast after 5 seconds
      setTimeout(() => setShowToast(false), 5000);
    }
  }, [user, loading, navigate]);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // User not logged in - show toast notification
  if (!user) {
    return (
      <>
        {showToast && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
            <div className="bg-white border-l-4 border-indigo-600 rounded-lg shadow-lg px-6 py-4 flex items-center gap-3 max-w-md">
              <i className="ri-lock-line text-2xl text-indigo-600"></i>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Login Required</p>
                <p className="text-gray-600 text-xs">Please log in to access grade content</p>
              </div>
              <button
                onClick={() => setShowToast(false)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // User logged in but not active - show upgrade screen
  if (!isActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 px-4 py-12">
        <div className="max-w-2xl w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-vip-crown-line text-4xl text-white"></i>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Upgrade Required
            </h1>
            
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              This content is available exclusively to active members. Unlock access to thousands of lessons, practice tests, and personalized learning tools.
            </p>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-3">What you'll get:</h3>
              <ul className="space-y-2 text-left max-w-md mx-auto">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-gray-700 text-sm">Full access to all grade levels and subjects</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-gray-700 text-sm">Interactive practice tests and worksheets</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-gray-700 text-sm">Personalized study plans and progress tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-gray-700 text-sm">Expert tutoring support when you need it</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#membership-tiers"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-vip-crown-line text-xl"></i>
                View Membership Plans
              </a>
              
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User is active - render protected content
  return <>{children}</>;
}